import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Alert, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Skeleton, ToggleButtonGroup, ToggleButton,
  Switch, FormControlLabel, Tooltip, TextField,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import {
  getDayTraderTickers, getDayTraderConfig, updateDayTraderConfig,
  getDayTraderPositions, getDayTraderLongTerm, getDayTraderSignals, getDayTraderStatus,
  getDayTraderMx1Status, getDayTraderMx1Config, updateDayTraderMx1Config,
  getDayTraderMx1Orders,
  getDayTraderPx1Status, getDayTraderPx1Config, updateDayTraderPx1Config,
  getCnDayTraderTickers, getCnDayTraderConfig, updateCnDayTraderConfig,
  getCnDayTraderSignals, getCnDayTraderStatus, getMarketEmotion,
} from '../utils/api';
import { useI18n } from '../utils/i18n';

const OK = '#00D9A6', DOWN = '#FF6B6B', WARN = '#FFD93D', MUTED = '#8b94ad', DIM = '#5c6480';
const VIOLET = '#6C63FF';

const LT_TIER_COLOR = {
  strong_buy: { bg: '#0f2e1a', fg: '#2ea043' },
  accumulate: { bg: '#0d2a2e', fg: '#39d0d8' },
  fair: { bg: '#2a2411', fg: '#d29922' },
  wait: { bg: '#2d1517', fg: '#f85149' },
  unknown: { bg: '#21262d', fg: '#8b949e' },
};

const CN_SESSION = '09:30–11:30 / 13:00–15:00 CST';

// mx-1 order reason-tag → pill style. Live config (rx + av2, EOD-flat) emits
// entry / exit / rebalance_*; the risk/EOD tags cover the default-off gates.
const REASON_TAG = {
  entry:          { label: 'Entry',     bg: 'rgba(0,217,166,.14)',  fg: '#00D9A6', bd: 'rgba(0,217,166,.4)' },
  exit:           { label: 'Exit',      bg: 'rgba(255,107,107,.13)', fg: '#FF6B6B', bd: 'rgba(255,107,107,.4)' },
  rebalance_add:  { label: 'Rebalance', bg: 'rgba(108,99,255,.16)', fg: '#b7b2ff', bd: '#6C63FF' },
  rebalance_trim: { label: 'Rebalance', bg: 'rgba(108,99,255,.16)', fg: '#b7b2ff', bd: '#6C63FF' },
  risk_brake:     { label: 'Risk',      bg: 'rgba(255,217,61,.14)', fg: '#FFD93D', bd: 'rgba(255,217,61,.45)' },
  overnight_stop: { label: 'EOD',       bg: 'rgba(255,217,61,.14)', fg: '#FFD93D', bd: 'rgba(255,217,61,.45)' },
  eod_flat:       { label: 'EOD',       bg: 'rgba(255,217,61,.14)', fg: '#FFD93D', bd: 'rgba(255,217,61,.45)' },
};
const REASON_TAG_FALLBACK = { label: 'Order', bg: 'rgba(139,148,173,.14)', fg: '#8b94ad', bd: 'rgba(139,148,173,.4)' };

function emotionColor(score) {
  if (score >= 75) return OK;
  if (score >= 55) return '#7BC67E';
  if (score >= 45) return WARN;
  if (score >= 25) return '#FF9F43';
  return DOWN;
}

function MiniGauge({ score, label }) {
  const s = Math.max(0, Math.min(100, score ?? 0));
  const color = emotionColor(s);
  const angle = Math.PI * (1 - s / 100);
  const x = 60 + 50 * Math.cos(angle);
  const y = 62 - 50 * Math.sin(angle);
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ position: 'relative', width: 120, height: 66, mx: 'auto' }}>
        <svg width="120" height="66" viewBox="0 0 120 66">
          <path d="M8 62 A52 52 0 0 1 112 62" fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="9" strokeLinecap="round" />
          <path d={`M8 62 A52 52 0 0 1 ${x.toFixed(1)} ${y.toFixed(1)}`} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" />
        </svg>
        <Typography sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, textAlign: 'center', fontSize: 24, fontWeight: 800, color }}>
          {s.toFixed(0)}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color, mt: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label || '—'}
      </Typography>
    </Box>
  );
}

// ── px1 post-market bot (engine b): money (budget) control + on/off + status.
// Reads /daytrader/px1_status (written each tick 16:00-20:00 ET) + px1_config. ──
function Px1Panel({ T }) {
  const [data, setData] = useState(null);
  const [mx1, setMx1] = useState(null);   // shared account numbers (same moomoo account)
  const [cfg, setCfg] = useState(null);
  const [reserveInput, setReserveInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try { setData(await getDayTraderPx1Status()); } catch (e) { /* no snapshot yet */ }
    try { setMx1(await getDayTraderMx1Status()); } catch (e) { /* mx-1 is the account reader */ }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  const loadCfg = useCallback(() => {
    getDayTraderPx1Config().then((c) => { setCfg(c); setReserveInput(String(c.reserve)); }).catch(() => {});
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const saveCfg = async (patch, okMsg) => {
    setSaving(true); setMsg('');
    try {
      const c = await updateDayTraderPx1Config(patch);
      setCfg(c); if (c.reserve != null) setReserveInput(String(c.reserve));
      setMsg(okMsg || T('Saved — applies next tick.', '已保存 — 下个周期生效。'));
    } catch (e) { setMsg(`Error: ${e.message}`); }
    finally { setSaving(false); setTimeout(() => setMsg(''), 7000); }
  };

  const money = (v) => (v == null ? '—' : `$${(Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
  const live = data && data.mode === 'live';
  const active = data && data.active !== false;
  const held = data && data.holdings ? Object.keys(data.holdings).length : 0;
  const tonight = (data && data.traded_tonight) || [];
  // shared account (one moomoo account): borrow mx-1's always-fresh reading
  const reserveNum = Number(cfg && cfg.reserve != null ? cfg.reserve : (data && data.reserve));
  const acctCash = mx1 && mx1.account_cash != null ? Number(mx1.account_cash) : null;
  const deployable = (acctCash != null && !isNaN(reserveNum)) ? Math.max(acctCash - reserveNum, 0) : null;

  return (
    <Box sx={{ mb: 2 }}>
      {/* header + status chips */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 800 }}>{T('px1 — Post-Market Bot', 'px1 — 盘后机器人')}</Typography>
        {data && <Chip size="small" label={live ? 'LIVE · REAL MONEY' : (data.mode === 'paper' ? 'PAPER · SIMULATE' : (data.mode || 'dry').toUpperCase())}
          sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: live ? '#2d1517' : 'rgba(108,99,255,0.14)',
                color: live ? DOWN : '#b7b2ff', border: `1px solid ${live ? DOWN : VIOLET}` }} />}
        {data && <Chip size="small" color={active ? 'success' : 'warning'} label={active ? T('ACTIVE', '运行中') : T('PAUSED', '已暂停')} sx={{ height: 22, fontSize: 11, fontWeight: 800 }} />}
        {data && data.stale && <Chip size="small" color="warning" label={T('idle · off-session', '空闲·非盘后')} sx={{ height: 22, fontSize: 10 }} />}
      </Box>

      {/* STRATEGY — the two layers, same card style as mx-1 (rx13/av2) */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        {[
          { tag: T('BUY / SELL SIGNAL', '买卖信号'), value: (data && data.signal) || 'rev1',
            desc: T('Fade an exhausted post-close flush (buy the dip, flat by 20:00)', '盘后急跌反弹（低吸，20:00前清仓）') },
          { tag: T('ALLOCATION', '仓位分配'), value: (data && data.allocator) || 'pv1',
            desc: T('Event-concentration: conviction × liquidity × earnings-volume', '事件集中：跌幅 × 流动性 × 财报放量') },
        ].map((s) => (
          <Card key={s.tag} sx={{ flex: 1, minWidth: 240, border: `1px solid ${VIOLET}`, bgcolor: 'rgba(108,99,255,0.06)' }}>
            <CardContent sx={{ py: '16px !important' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: VIOLET, mb: 0.5 }}>{s.tag}</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15 }}>{s.value}</Typography>
              <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.5 }}>{s.desc}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Typography sx={{ fontSize: 12, color: MUTED, mb: 1.5 }}>
        {T('Trades only the post-market window (16:00–20:00 ET), flat by close — never holds the overnight gap. Deploys account cash minus the reserve below, separate from your RTH / manual cash.',
           '仅在盘后（美东 16:00–20:00）交易，收盘前清仓，不持有隔夜。动用"账户现金 − 下方保留金"，与日内 / 手动资金分开。')}
      </Typography>

      {/* stat strip removed — account numbers are unified on the mx-1 card above. */}

      {/* config row — reserve + on/off + off-tonight (mx-1 style) */}
      <Card>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: '14px !important' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: DIM }}>{T('Reserve for px1', 'px1 保留现金')}</Typography>
          <TextField size="small" type="number" value={reserveInput} disabled={saving || !cfg}
            onChange={(e) => setReserveInput(e.target.value)}
            inputProps={{ min: cfg?.reserve_min ?? 0, max: cfg?.reserve_max ?? 1000000, step: 500 }}
            sx={{ width: 150 }} InputProps={{ startAdornment: <span style={{ color: DIM, marginRight: 4 }}>$</span> }} />
          <Button size="small" variant="outlined" disabled={saving || reserveInput === ''}
            onClick={() => saveCfg({ reserve: parseFloat(reserveInput) },
              T('Reserve saved — px1 leaves this much untouched; higher = px1 trades smaller.', '已保存 — px1 保留这些现金不动；越高则交易越小。'))}>
            {T('Set reserve', '设置')}
          </Button>
          {deployable != null && (
            <Typography sx={{ fontSize: 11, color: MUTED }}>
              {T(`→ px1 can use ≈ ${money(deployable)} (cash − reserve)`, `→ px1 可用 ≈ ${money(deployable)}（现金 − 保留）`)}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <FormControlLabel sx={{ m: 0 }}
            control={<Switch size="small" checked={!!cfg?.enabled} disabled={saving || !cfg}
              onChange={(e) => saveCfg({ enabled: e.target.checked, ...(e.target.checked ? { pause_until: null } : {}) },
                e.target.checked ? T('px1 ON.', 'px1 已开启。') : T('px1 OFF — no new post-market trades.', 'px1 已关闭 — 不再盘后开仓。'))} />}
            label={<Typography sx={{ fontSize: 12, fontWeight: 700, color: cfg?.enabled ? OK : WARN }}>{cfg?.enabled ? T('ON', '开') : T('OFF', '关')}</Typography>} />
          <Button size="small" variant="outlined" color="warning" disabled={saving || !cfg?.enabled}
            onClick={() => saveCfg({ pause_today: true }, T('Off for tonight.', '今晚暂停。'))}>
            {T('Off tonight', '今晚关闭')}
          </Button>
          {cfg?.pause_until && <Typography sx={{ fontSize: 11, color: WARN, width: '100%' }}>{T(`paused until ${cfg.pause_until}`, `暂停至 ${cfg.pause_until}`)}</Typography>}
          {data && !data.stale && (
            <Typography sx={{ fontSize: 11.5, color: MUTED, width: '100%' }}>
              {T(`Holding ${held} · traded tonight: ${tonight.length ? tonight.join(', ') : 'none'}`, `持仓 ${held} · 今晚已交易：${tonight.length ? tonight.join('、') : '无'}`)}
            </Typography>
          )}
          {msg && <Typography sx={{ fontSize: 11, width: '100%', color: msg.startsWith('Error') ? DOWN : OK }}>{msg}</Typography>}
        </CardContent>
      </Card>
    </Box>
  );
}

// ── mx-1 budget bot portfolio dashboard (the live US trader; rx6s fleet
// retired). Reads /daytrader/mx1_status (written each tick by the bot) and
// renders mode, equity/cash/P&L, holdings with weights, and recent orders. ──
function Mx1Panel({ T }) {
  const [data, setData] = useState(null);
  const [orders7, setOrders7] = useState(null);  // durable 7-day order history (with reasons)
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [cfg, setCfg] = useState(null);
  const [reserveInput, setReserveInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [cfgMsg, setCfgMsg] = useState('');

  const load = useCallback(async () => {
    try { setData(await getDayTraderMx1Status()); setErr(''); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
    // durable 7-day order log (separate endpoint; fail-soft — the card falls
    // back to the status snapshot if this is empty/unavailable).
    try { setOrders7(await getDayTraderMx1Orders(7)); } catch (e) { /* keep prior */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const loadCfg = useCallback(() => {
    getDayTraderMx1Config()
      .then((c) => { setCfg(c); setReserveInput(String(c.reserve)); })
      .catch(() => {});
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const saveCfg = async (patch, okMsg) => {
    setSaving(true); setCfgMsg('');
    try {
      const c = await updateDayTraderMx1Config(patch);
      setCfg(c); if (c.reserve != null) setReserveInput(String(c.reserve));
      setCfgMsg(okMsg || T('Saved — applies next tick.', '已保存 — 下个周期生效。'));
    } catch (e) { setCfgMsg(`Error: ${e.message}`); loadCfg(); }
    finally { setSaving(false); setTimeout(() => setCfgMsg(''), 7000); }
  };

  // Trend-mute override toggle with OPTIMISTIC feedback: flip cfg.mute_allow
  // locally at once so the button reflects the click immediately, then persist
  // (saveCfg reconciles cfg from the server response; its catch reloads to
  // revert). The actual trading effect still applies on mx-1's next tick.
  const toggleMute = (tk, allow) => {
    if (saving) return;
    setCfg((c) => (c ? { ...c, mute_allow: allow
      ? [...new Set([...(c.mute_allow || []), tk])]
      : (c.mute_allow || []).filter((x) => x !== tk) } : c));
    saveCfg({ mute_tickers: { [tk]: allow } },
      allow ? T(`${tk} manually allowed — mx-1 may trade it (applies next tick).`, `${tk} 已手动放行 — mx-1 可交易（下个周期生效）。`)
            : T(`${tk} back under trend mute (applies next tick).`, `${tk} 已恢复趋势静音（下个周期生效）。`));
  };

  const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />;
  if (err) return <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>;
  if (!data || data.running === false) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {T('mx-1 budget bot has no snapshot yet. It writes one each tick during US market hours (13:30–20:00 UTC).',
           'mx-1 预算机器人暂无快照。美股盘中（UTC 13:30–20:00）每个周期写入一次。')}
      </Alert>
    );
  }

  const live = data.mode === 'live';
  const active = data.active !== false;
  const holdings = data.holdings || [];
  // "durable" = the 7-day endpoint RESPONDED (even with zero orders), so the
  // card shows the 7-day framing while empty. Only fall back to the volatile
  // status snapshot when the endpoint is unreachable/errored (orders7 null).
  const durable = !!(orders7 && Array.isArray(orders7.orders));
  const orders = durable ? orders7.orders : (data.orders || []).slice().reverse(); // newest-first
  // Group newest-first orders by calendar day, with per-day subtotals.
  const orderGroups = [];
  orders.forEach((o) => {
    const d = String(o.ts || '').substring(0, 10);
    let g = orderGroups[orderGroups.length - 1];
    if (!g || g.day !== d) { g = { day: d, rows: [], n: 0, realized: 0 }; orderGroups.push(g); }
    g.rows.push(o); g.n += 1;
    if (typeof o.realized_pnl === 'number') g.realized += o.realized_pnl;
  });
  const moneyN = (v) => (v == null ? '—' : money(v));

  const Stat = ({ k, children }) => (
    <Card sx={{ flex: 1, minWidth: 140 }}>
      <CardContent sx={{ py: '14px !important' }}>
        <Typography sx={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k}</Typography>
        <Box sx={{ mt: 0.4 }}>{children}</Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* status chips — live/paper, active/paused, staleness */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip size="small" label={live ? 'LIVE · REAL MONEY' : 'PAPER · SIMULATE'}
          sx={{ height: 22, fontSize: 11, fontWeight: 800,
                bgcolor: live ? '#2d1517' : 'rgba(108,99,255,0.14)',
                color: live ? DOWN : '#b7b2ff', border: `1px solid ${live ? DOWN : VIOLET}` }} />
        <Chip size="small" color={active ? 'success' : 'warning'}
          label={active ? T('ACTIVE', '运行中') : T('PAUSED', '已暂停')}
          sx={{ height: 22, fontSize: 11, fontWeight: 800 }} />
        {data.stale && <Chip size="small" color="warning" label={T(`stale ${Math.round((data.age_sec || 0) / 60)}m`, `过期 ${Math.round((data.age_sec || 0) / 60)}分`)} sx={{ height: 22, fontSize: 10 }} />}
      </Box>

      {/* STRATEGY — the two layers that define how mx-1 trades, shown large */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        {[
          { tag: T('BUY / SELL SIGNAL', '买卖信号'),
            value: data.signal || 'rx',
            desc: T('Decides WHEN to be long each stock', '决定每只股票何时持有') },
          { tag: T('ALLOCATION', '仓位分配'),
            value: data.allocation_label || data.allocation || 'inverse-vol',
            sub: data.allocation ? data.allocation.toUpperCase() : null,
            desc: T('Decides HOW MUCH capital each stock gets', '决定每只股票分配多少资金') },
        ].map((s) => (
          <Card key={s.tag} sx={{ flex: 1, minWidth: 240,
                  border: `1px solid ${VIOLET}`, bgcolor: 'rgba(108,99,255,0.06)' }}>
            <CardContent sx={{ py: '16px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8,
                                  textTransform: 'uppercase', color: VIOLET }}>{s.tag}</Typography>
                {s.sub && (
                  <Box component="span" sx={{ fontSize: 10, fontWeight: 800, color: '#b7b2ff',
                        bgcolor: 'rgba(108,99,255,0.18)', px: 0.7, py: '1px', borderRadius: '6px' }}>{s.sub}</Box>
                )}
              </Box>
              <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15 }}>{s.value}</Typography>
              <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.5 }}>{s.desc}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* TREND-MUTE — regime-gated over-extension gate (rx-side). Hidden when OFF.
          One panel: a row per over-extended name with a segmented Muted|Allowed
          toggle (manual override). Allowed = exempt from the gate (mx-1 may trade). */}
      {data.trend_mute?.enabled && (() => {
        const tm = data.trend_mute;
        // Row SET (which names are over-extended today) comes from the status —
        // it's stable across a click. Toggle STATE comes from cfg.mute_allow, which
        // updates the instant the POST returns (optimistic), so the button is
        // responsive and doesn't wait for mx-1's next tick + status poll.
        const allowSet = new Set(cfg?.mute_allow || []);
        const rows = (tm.regime_on ? (tm.extended || []) : []).slice().sort();
        const dormant = (cfg?.mute_allow || []).filter((t) => !rows.includes(t)).slice().sort();
        const anyMuted = rows.some((t) => !allowSet.has(t));
        return (
          <Card sx={{ mb: 2, border: `1px solid ${anyMuted ? WARN : VIOLET}`,
                      bgcolor: anyMuted ? 'rgba(245,166,35,0.06)' : 'rgba(108,99,255,0.05)' }}>
            <CardContent sx={{ py: '14px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.9, textTransform: 'uppercase',
                                  color: anyMuted ? WARN : VIOLET }}>{T('Trend mute', '趋势静音')}</Typography>
                <Box component="span" sx={{ fontSize: 10.5, fontWeight: 800, px: 1, py: '2px', borderRadius: '20px',
                  ...(tm.regime_on
                    ? { color: WARN, bgcolor: 'rgba(255,217,61,0.14)', border: '1px solid rgba(255,217,61,0.4)' }
                    : { color: '#9a93ff', bgcolor: 'rgba(108,99,255,0.14)', border: '1px solid rgba(108,99,255,0.4)' }) }}>
                  {tm.regime_on ? T('REVERSION REGIME', '均值回归行情') : T('MOMENTUM / NEUTRAL — DORMANT', '动量/中性 — 休眠')}
                </Box>
              </Box>
              <Typography sx={{ fontSize: 12, color: MUTED, mb: rows.length ? 1.3 : 0 }}>
                {T('Skips an over-extended name from buys until it cools. Flip any to Allowed to override and let mx-1 trade it.',
                   '对超涨股暂停买入，待其回落。将任一只切换为“放行”即可覆盖，让 mx-1 继续交易。')}
              </Typography>

              {rows.length ? rows.map((tk) => {
                const allowed = allowSet.has(tk);
                return (
                  <Box key={tk} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: '9px', mb: 0.8,
                       border: '1px solid #242838', borderRadius: '9px', bgcolor: 'rgba(0,0,0,0.18)' }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 800, minWidth: 56, color: allowed ? OK : WARN }}>{tk}</Typography>
                    <Typography sx={{ fontSize: 11, color: DIM, flexGrow: 1 }}>
                      {allowed ? T('over-extended · you are allowing it', '超涨 · 你已放行')
                               : T('over-extended · gate is skipping it', '超涨 · 门控跳过中')}
                    </Typography>
                    <Box sx={{ display: 'inline-flex', border: '1px solid #2d3246', borderRadius: '8px',
                         overflow: 'hidden', opacity: saving ? 0.6 : 1 }}>
                      <Box onClick={() => toggleMute(tk, false)}
                        sx={{ fontSize: 11.5, fontWeight: 800, px: 1.6, py: '5px', cursor: saving ? 'default' : 'pointer',
                              ...(!allowed ? { bgcolor: WARN, color: '#1a1205' } : { color: '#6b7390' }) }}>
                        {T('Muted', '静音')}
                      </Box>
                      <Box onClick={() => toggleMute(tk, true)}
                        sx={{ fontSize: 11.5, fontWeight: 800, px: 1.6, py: '5px', cursor: saving ? 'default' : 'pointer',
                              ...(allowed ? { bgcolor: OK, color: '#06241c' } : { color: '#6b7390' }) }}>
                        {T('Allowed', '放行')}
                      </Box>
                    </Box>
                  </Box>
                );
              }) : (
                <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                  {tm.regime_on ? T('Reversion regime — no over-extended names right now.', '均值回归行情 — 当前无超涨股。')
                                : T('Momentum / neutral regime — nothing muted; all names trade normally.', '动量/中性行情 — 无静音，所有股票正常交易。')}
                </Typography>
              )}

              {dormant.length ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', mt: 1.2 }}>
                  <Typography sx={{ fontSize: 11, color: DIM }}>
                    {T('Standing overrides (not over-extended now):', '常设放行（当前未超涨）：')}
                  </Typography>
                  {dormant.map((tk) => (
                    <Box key={tk} component="span"
                      onClick={() => toggleMute(tk, false)}
                      title={T('click to remove this override (re-mute when over-extended)', '点击移除放行（超涨时恢复静音）')}
                      sx={{ fontSize: 10.5, fontWeight: 800, color: OK, border: `1px solid ${OK}`, borderRadius: '6px',
                            px: 0.9, py: '1px', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                      {tk} ✓ {T('remove', '移除')}
                    </Box>
                  ))}
                </Box>
              ) : null}

              <Typography sx={{ fontSize: 10.5, color: DIM, mt: 1.2 }}>
                {T('A click flips a name between Muted and Allowed — takes effect next tick, no restart.',
                   '点击即在“静音”与“放行”间切换 — 下一个 tick 生效，无需重启。')}
              </Typography>
            </CardContent>
          </Card>
        );
      })()}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Stat k={T('Equity', '净值')}><Typography sx={{ fontSize: 23, fontWeight: 700 }}>{money(data.equity)}</Typography></Stat>
        <Stat k={T('Account cash', '账户现金')}><Typography sx={{ fontSize: 23, fontWeight: 700 }}>{moneyN(data.account_cash)}</Typography></Stat>
        <Stat k={T('Deployed', '已投入')}><Typography sx={{ fontSize: 23, fontWeight: 700 }}>{money(data.deployed)}</Typography></Stat>
        <Stat k={T('Holdings', '持仓数')}><Typography sx={{ fontSize: 23, fontWeight: 700 }}>{data.n_holdings ?? holdings.length}</Typography></Stat>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: '14px !important' }}>
          <FormControlLabel sx={{ m: 0 }}
            control={<Switch size="small" checked={!!cfg?.enabled} disabled={saving || !cfg}
              onChange={(e) => saveCfg({ enabled: e.target.checked, ...(e.target.checked ? { pause_until: null } : {}) },
                e.target.checked ? T('mx-1 ON.', 'mx-1 已开启。') : T('mx-1 OFF — you have the account. It won\'t buy or sell.', 'mx-1 已关闭 — 账户归你，它不再买卖。'))} />}
            label={<Typography sx={{ fontSize: 12, fontWeight: 700, color: cfg?.enabled ? OK : WARN }}>
              {cfg?.enabled ? T('mx-1 ON', 'mx-1 开') : T('mx-1 OFF', 'mx-1 关')}</Typography>} />
          <Button size="small" variant="outlined" color="warning" disabled={saving || !cfg?.enabled}
            onClick={() => saveCfg({ pause_today: true }, T('Paused for today — resume with the toggle.', '今日已暂停 — 用开关恢复。'))}>
            {T('Off for today', '今日关闭')}
          </Button>
          {cfg?.pause_until && (
            <Typography sx={{ fontSize: 11, color: WARN }}>{T(`paused until ${cfg.pause_until}`, `暂停至 ${cfg.pause_until}`)}</Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: DIM }}>
            {T('Reserve', '保留现金')}
          </Typography>
          <TextField size="small" type="number" value={reserveInput} disabled={saving}
            onChange={(e) => setReserveInput(e.target.value)}
            inputProps={{ min: cfg?.reserve_min ?? 0, max: cfg?.reserve_max ?? 1000000, step: 500 }}
            sx={{ width: 140 }} />
          <Button size="small" variant="outlined" disabled={saving || reserveInput === ''}
            onClick={() => saveCfg({ reserve: parseFloat(reserveInput) }, T('Reserve saved — kept for your manual trades.', '已保存 — 为你的手动交易保留。'))}>
            {T('Set reserve', '设置')}
          </Button>
          {cfgMsg && (
            <Typography sx={{ fontSize: 11, width: '100%', color: cfgMsg.startsWith('Error') ? DOWN : OK }}>{cfgMsg}</Typography>
          )}
        </CardContent>
      </Card>

      {cfg?.universe && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: DIM, mb: 1 }}>
              {T('Per-stock — mx-1 on/off', '每只股票 — mx-1 开关')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {cfg.universe.map((tk) => {
                const off = (cfg.disabled || []).includes(tk);
                const held = holdings.some((h) => h.ticker === tk);
                return (
                  <Box key={tk}
                    onClick={() => !saving && saveCfg({ tickers: { [tk]: off } },
                      T(`${tk} is now ${off ? 'ON' : 'OFF'} in mx-1`, `${tk} 在 mx-1 中已${off ? '开启' : '关闭'}`))}
                    sx={{ cursor: 'pointer', userSelect: 'none', px: 1.1, py: '5px', borderRadius: '7px',
                          border: `1px solid ${off ? DIM : OK}`, bgcolor: off ? 'transparent' : 'rgba(0,217,166,0.08)',
                          display: 'flex', alignItems: 'center', gap: 0.6, opacity: saving ? 0.6 : 1 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: off ? DIM : OK }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: off ? MUTED : 'text.primary' }}>{tk}</Typography>
                    {held && <Box component="span" sx={{ fontSize: 9, color: VIOLET, fontWeight: 800 }}>●</Box>}
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ fontSize: 10.5, color: DIM, mt: 1 }}>
              {T("Grey = OFF (mx-1 won't buy or sell it — you manage it). ● = currently held.",
                 '灰色 = 关闭（mx-1 不买不卖，由你管理）。● = 当前持仓。')}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: DIM, mb: 1 }}>
            {T('Holdings', '持仓')}
          </Typography>
          {holdings.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2.5 }}>
              {T('Flat — no open positions.', '空仓 — 无持仓。')}
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
              <Table size="small">
                <TableHead><TableRow>
                  {[T('Ticker', '代码'), T('Shares', '股数'), T('Avg cost', '成本'), T('Last', '现价'), T('Value', '市值'), T('Weight', '权重'), T('Unreal. P&L', '浮动盈亏')].map((h, j) => (
                    <TableCell key={j} align={j === 0 ? 'left' : 'right'} sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {holdings.map((h) => {
                    const upl = Number(h.unrealized_pnl) || 0;
                    return (
                      <TableRow key={h.ticker} hover>
                        <TableCell sx={{ py: 0.6, fontWeight: 700, fontSize: 13 }}>{h.ticker}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: 12 }}>{h.shares}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: 12 }}>${Number(h.avg_cost).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: 12 }}>${Number(h.last).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: 12 }}>{money(h.mkt_value)}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: 12 }}>{Number(h.weight_pct).toFixed(1)}%</TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: 12, fontWeight: 600, color: upl >= 0 ? OK : DOWN }}>
                          {upl >= 0 ? '+' : ''}{money(upl)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: DIM }}>
              {T('Recent Orders', '近期订单')}
            </Typography>
            <Chip size="small" label={durable ? T('last 7 days', '近 7 天') : T('today', '今日')}
              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(108,99,255,0.14)', color: '#b7b2ff', border: `1px solid ${VIOLET}` }} />
          </Box>
          {orders.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2.5 }}>
              {durable ? T('No orders in the last 7 days.', '近 7 天暂无订单。') : T('No orders yet today.', '今日暂无订单。')}
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none', maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {[T('Time', '时间'), T('Action', '动作'), T('Ticker', '代码'), T('Shares', '股数'), T('Price', '价格'), T('Reason', '原因')].map((h, j) => (
                    <TableCell key={j} align={j >= 3 && j <= 4 ? 'right' : 'left'} sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {orderGroups.map((g) => (
                    <Fragment key={g.day}>
                      {durable && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 0.75, bgcolor: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                            <Typography component="span" sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: MUTED }}>
                              {g.day}
                            </Typography>
                            <Typography component="span" sx={{ fontSize: 11, color: DIM, ml: 1.25, fontWeight: 600 }}>
                              {g.n} {T('orders', '笔')}
                            </Typography>
                            {g.realized !== 0 && (
                              <Typography component="span" sx={{ fontSize: 11, ml: 1.25, fontWeight: 700, color: g.realized >= 0 ? OK : DOWN }}>
                                {T('realized', '已实现')} {g.realized >= 0 ? '+' : ''}{money(g.realized)}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                      {g.rows.map((o, i) => {
                        const buy = o.action === 'BUY';
                        const tag = REASON_TAG[o.reason_tag] || (o.reason_tag ? REASON_TAG_FALLBACK : null);
                        return (
                          <TableRow key={`${g.day}-${i}`}>
                            <TableCell sx={{ py: 0.5, fontSize: 12, color: MUTED }}>{String(o.ts || '').substring(11, 16)}</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: 12, fontWeight: 700, color: buy ? OK : DOWN }}>{o.action}</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: 12, fontWeight: 600 }}>{o.ticker}</TableCell>
                            <TableCell align="right" sx={{ py: 0.5, fontSize: 12 }}>{o.shares}</TableCell>
                            <TableCell align="right" sx={{ py: 0.5, fontSize: 12 }}>${Number(o.price).toFixed(2)}</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: 12 }}>
                              {tag ? (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                  <Box component="span" sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
                                    px: 0.75, py: '2px', borderRadius: 999, whiteSpace: 'nowrap',
                                    bgcolor: tag.bg, color: tag.fg, border: `1px solid ${tag.bd}` }}>
                                    {tag.label}
                                  </Box>
                                  {o.reason && (
                                    <Typography component="span" sx={{ fontSize: 11.5, color: MUTED }}>
                                      {String(o.reason).replace(/^.*?—\s*/, '')}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography component="span" sx={{ fontSize: 11.5, color: DIM }}>—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default function DayTraderPage() {
  const { lang } = useI18n();
  const zh = lang === 'zh';
  const T = (en, cn) => (zh ? cn : en);

  const [market, setMarket] = useState('US');
  const isUS = market === 'US';

  // Fleet meta/config
  const [tickers, setTickers] = useState([]);
  const [strategies, setStrategies] = useState({});
  const [meta, setMeta] = useState({});
  const [config, setConfig] = useState(null);
  const [loadingFleet, setLoadingFleet] = useState(true);
  const [error, setError] = useState('');

  // Secondary (fail soft)
  const [positions, setPositions] = useState({});
  const [longTerm, setLongTerm] = useState({});
  const [fleetSignals, setFleetSignals] = useState({});
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [emotion, setEmotion] = useState(null);

  // Selected-ticker detail
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState(null);
  const [detailSignals, setDetailSignals] = useState([]);
  const [equityCurve, setEquityCurve] = useState([]);
  const [days, setDays] = useState(7);

  // Config save
  const [savingCfg, setSavingCfg] = useState(false);
  const [cfgMsg, setCfgMsg] = useState('');
  const [newTicker, setNewTicker] = useState('');

  const api = useMemo(() => (isUS
    ? { tickers: getDayTraderTickers, config: getDayTraderConfig, update: updateDayTraderConfig, signals: getDayTraderSignals, status: getDayTraderStatus }
    : { tickers: getCnDayTraderTickers, config: getCnDayTraderConfig, update: updateCnDayTraderConfig, signals: getCnDayTraderSignals, status: getCnDayTraderStatus }), [isUS]);

  const fetchFleet = useCallback(async () => {
    setLoadingFleet(true);
    setError('');
    try {
      const [tk, cfg] = await Promise.all([api.tickers(), api.config()]);
      const list = tk.tickers || [];
      setTickers(list);
      setStrategies(tk.strategies || {});
      setMeta(tk.meta || {});
      setConfig(cfg);
      // Default selection = first configured ticker in this market.
      setSelected((prev) => (prev && list.includes(prev) ? prev : (list[0] || null)));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingFleet(false);
    }
  }, [api]);

  const fetchPositions = useCallback(async () => {
    if (!isUS) { setPositions({}); return; }
    try { const p = await getDayTraderPositions(); setPositions(p.positions || {}); } catch { /* soft */ }
  }, [isUS]);

  const fetchLongTerm = useCallback(async () => {
    if (!isUS) { setLongTerm({}); return; }
    try { const lt = await getDayTraderLongTerm(); setLongTerm(lt.ratings || {}); } catch { /* soft */ }
  }, [isUS]);

  const fetchEmotion = useCallback(async () => {
    try { setEmotion(await getMarketEmotion(market)); } catch { setEmotion(null); }
  }, [market]);

  const fetchFleetSignals = useCallback(async (list) => {
    if (!list || list.length === 0) return;
    setSignalsLoading(true);
    const results = await Promise.allSettled(
      list.map((tk) => api.signals(tk, 5).then((r) => [tk, r.signals || []]))
    );
    const map = {};
    results.forEach((res) => { if (res.status === 'fulfilled') { const [tk, sigs] = res.value; map[tk] = sigs; } });
    setFleetSignals(map);
    setSignalsLoading(false);
  }, [api]);

  // Selected-ticker detail: status + equity curve + recent activity.
  const fetchDetail = useCallback(async () => {
    if (!selected) { setStatus(null); setDetailSignals([]); setEquityCurve([]); return; }
    try {
      const [st, sg] = await Promise.all([api.status(selected), api.signals(selected, days)]);
      setStatus(st);
      setDetailSignals(sg.signals || []);
      setEquityCurve(sg.equity_curve || []);
    } catch (e) { setError(e.message); }
  }, [api, selected, days]);

  // Reset per-market and load on toggle.
  useEffect(() => {
    setPositions({}); setLongTerm({}); setFleetSignals({}); setStatus(null);
    setDetailSignals([]); setEquityCurve([]);
    fetchFleet(); fetchEmotion();
  }, [market]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loadingFleet || tickers.length === 0) return;
    fetchPositions(); fetchLongTerm(); fetchFleetSignals(tickers);
  }, [loadingFleet, tickers, fetchPositions, fetchLongTerm, fetchFleetSignals]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // Light refresh: positions 60s; detail + fleet signals 5min.
  useEffect(() => {
    const id = setInterval(() => { fetchPositions(); }, 60000);
    return () => clearInterval(id);
  }, [fetchPositions]);
  useEffect(() => {
    const id = setInterval(() => { fetchDetail(); fetchFleetSignals(tickers); }, 300000);
    return () => clearInterval(id);
  }, [fetchDetail, fetchFleetSignals, tickers]);

  const saveConfig = async (patch) => {
    setSavingCfg(true); setCfgMsg('');
    try {
      const next = await api.update(patch);
      setConfig(next);
      setCfgMsg(T('Saved — live runners pick this up shortly.', '已保存 — 实盘稍后生效。'));
      fetchFleet();
    } catch (e) {
      setCfgMsg(`Error: ${e.message}`);
    } finally {
      setSavingCfg(false);
      setTimeout(() => setCfgMsg(''), 6000);
    }
  };

  const stocks = useMemo(() => config?.stocks || {}, [config]);
  const configuredList = useMemo(() => Object.keys(stocks).sort(), [stocks]);
  // Group the live table by GICS sector (US only — CN names have no sector meta).
  // Sector comes from the backend /daytrader/tickers `meta`; order mirrors the
  // email's day_trading/sector_map.SECTOR_ORDER. CN renders as one flat group.
  const groupedList = useMemo(() => {
    if (!isUS) return [[null, configuredList]];
    const ORDER = ['Information Technology', 'Communication Services', 'Consumer Discretionary',
      'Energy', 'Financials', 'Materials', 'Industrials', 'Health Care',
      'Consumer Staples', 'Utilities', 'Real Estate', 'Other'];
    const buckets = {};
    configuredList.forEach((tk) => {
      const sec = meta[tk]?.sector || 'Other';
      (buckets[sec] = buckets[sec] || []).push(tk);
    });
    const out = [];
    ORDER.forEach((sec) => { if (buckets[sec]) out.push([sec, buckets[sec]]); });
    Object.keys(buckets).sort().forEach((sec) => { if (!ORDER.includes(sec)) out.push([sec, buckets[sec]]); });
    return out;
  }, [isUS, configuredList, meta]);
  const enabledCount = configuredList.filter((tk) => stocks[tk].enabled).length;
  const cap = config?.max_shares_cap || 100;

  const heldFromSignals = useMemo(() => {
    const held = new Set();
    Object.entries(fleetSignals).forEach(([tk, sigs]) => {
      if (!sigs || sigs.length === 0) return;
      const last = sigs[sigs.length - 1];
      if (last && (last.action === 'BUY' || last.action === 'SELL')) held.add(tk);
    });
    return held;
  }, [fleetSignals]);

  const openCount = isUS
    ? Object.values(positions).filter((p) => p && p.entry_price).length
    : heldFromSignals.size;

  const mergedSignals = useMemo(() => {
    const all = [];
    Object.entries(fleetSignals).forEach(([tk, sigs]) => (sigs || []).forEach((s) => all.push({ ...s, ticker: tk })));
    all.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
    return all;
  }, [fleetSignals]);
  const latestDay = mergedSignals.length ? (mergedSignals[0].timestamp || '').substring(0, 10) : null;
  const todaySignals = latestDay ? mergedSignals.filter((s) => (s.timestamp || '').startsWith(latestDay)) : [];
  const dayRealized = todaySignals.reduce((acc, s) => acc + (s.trade?.pnl_pct || 0), 0);
  const hasRealized = todaySignals.some((s) => s.trade?.pnl_pct != null);

  const ccy = isUS ? '$' : '¥';
  const nameOf = (tk) => (meta[tk]?.name ? meta[tk].name : null);
  const labelOf = (tk) => (meta[tk]?.name ? `${meta[tk].name} ${tk}` : tk);

  // ── Cells ──
  const PositionCell = ({ ticker }) => {
    const p = isUS ? positions[ticker] : null;
    if (isUS && p && p.entry_price) {
      const isLong = p.side === 'long';
      const sideColor = isLong ? OK : DOWN;
      const pnl = p.pnl_pct;
      const pnlColor = pnl == null ? MUTED : pnl >= 0 ? OK : DOWN;
      return (
        <Box component="span" sx={{
          display: 'inline-block', bgcolor: isLong ? '#0f2e1a' : '#2d1517', border: `1px solid ${sideColor}`,
          borderRadius: '7px', px: 1, py: '3px', lineHeight: 1.3, whiteSpace: 'nowrap',
        }}>
          <Box sx={{ color: sideColor, fontWeight: 800, fontSize: 10 }}>● {p.side.toUpperCase()}</Box>
          <Box sx={{ fontSize: 11 }}>
            @ {ccy}{p.entry_price.toFixed(2)}
            {pnl != null && <Box component="span" sx={{ color: pnlColor, ml: 0.5 }}>{pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}%</Box>}
          </Box>
        </Box>
      );
    }
    if (!isUS && heldFromSignals.has(ticker)) {
      return (
        <Box component="span" sx={{
          display: 'inline-block', bgcolor: 'rgba(108,99,255,0.14)', border: `1px solid ${VIOLET}`,
          borderRadius: '7px', px: 1.1, py: '4px', color: '#b7b2ff', fontSize: 10, fontWeight: 800, lineHeight: 1,
        }}>HELD</Box>
      );
    }
    return (
      <Box component="span" sx={{
        display: 'inline-block', border: `1px solid ${DIM}`, borderRadius: '7px',
        px: 1.1, py: '4px', color: MUTED, fontSize: 10, fontWeight: 700, lineHeight: 1,
      }}>{T('flat', '空仓')}</Box>
    );
  };

  const LongTermCell = ({ ticker }) => {
    const lt = longTerm[ticker];
    if (!lt) return <Typography sx={{ fontSize: 11, color: MUTED }}>—</Typography>;
    const c = LT_TIER_COLOR[lt.tier] || LT_TIER_COLOR.unknown;
    const label = (lt.tier || 'unknown').replace('_', ' ').toUpperCase();
    const tip = [
      lt.pullback_pct != null ? `Dip off high: −${lt.pullback_pct.toFixed(1)}%` : null,
      lt.upside_to_high_pct != null ? `Upside to prior high: +${lt.upside_to_high_pct.toFixed(0)}%` : null,
      lt.downside_to_low_pct != null ? `Downside to support: −${lt.downside_to_low_pct.toFixed(0)}%${lt.support_broken ? ' ⚠ broken' : ''}` : null,
      lt.rsi != null ? `RSI: ${lt.rsi.toFixed(0)}` : null,
      lt.reason,
    ].filter(Boolean).join(' · ');
    return (
      <Tooltip arrow placement="top" title={tip}>
        <Box component="span" sx={{
          bgcolor: c.bg, color: c.fg, fontWeight: 800, fontSize: 10.5,
          px: 0.9, py: '2px', borderRadius: '6px', border: `1px solid ${c.fg}`, whiteSpace: 'nowrap',
        }}>{lt.emoji ? `${lt.emoji} ` : ''}{label}{lt.score != null ? ` ${lt.score}` : ''}</Box>
      </Tooltip>
    );
  };

  const Stat = ({ k, children }) => (
    <Card sx={{ flex: 1, minWidth: 140 }}>
      <CardContent sx={{ py: '14px !important' }}>
        <Typography sx={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k}</Typography>
        <Box sx={{ mt: 0.4 }}>{children}</Box>
      </CardContent>
    </Card>
  );

  const SectionLabel = ({ children }) => (
    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: DIM }}>
      {children}
    </Typography>
  );

  const emoScore = emotion?.emotion_score;
  const emoLabel = zh ? (emotion?.label_cn || '') : (emotion?.label || '');

  const position = status?.position;
  const cumulative = status?.cumulative || {};
  const today = status?.today || {};

  const colSpan = isUS ? 6 : 4;

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h4">{T('Day Trader', '日内交易')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
            {isUS
              ? T('mx-1 — trades your whole account, minus a cash reserve you keep.', 'mx-1 — 交易你的整个账户，保留一部分现金给你自己。')
              : T('Your live fleet and per-stock detail in one place.', '实盘机队与个股详情，合于一处。')}
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={market} onChange={(_, v) => { if (v) setMarket(v); }}>
          <ToggleButton value="US" sx={{ px: 2 }}>🇺🇸 US</ToggleButton>
          <ToggleButton value="CN" sx={{ px: 2 }}>🇨🇳 CN</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {!isUS && (
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          {T('CN trader sends email alerts on signals only — no live orders. ', 'A股交易器仅在产生信号时发邮件提醒 — 不实盘下单。')}
          {T(`Trading session ${CN_SESSION}.`, `交易时段 ${CN_SESSION}。`)}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isUS && <Mx1Panel T={T} />}
      {isUS && <Px1Panel T={T} />}

      {!isUS && (<>
      {/* ── Stat strip ── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Stat k={T('Open positions', '持仓')}>
          <Typography sx={{ fontSize: 26, fontWeight: 700 }}>
            {openCount}<Typography component="span" sx={{ fontSize: 14, color: DIM }}> / {configuredList.length}</Typography>
          </Typography>
        </Stat>
        <Stat k={T('Day realized P&L', '当日已实现')}>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color: !hasRealized ? MUTED : dayRealized >= 0 ? OK : DOWN }}>
            {hasRealized ? `${dayRealized >= 0 ? '+' : ''}${dayRealized.toFixed(1)}%` : '—'}
          </Typography>
        </Stat>
        <Stat k={T('Signals today', '今日信号')}>
          <Typography sx={{ fontSize: 26, fontWeight: 700 }}>
            {signalsLoading ? <Skeleton width={30} /> : todaySignals.length}
          </Typography>
        </Stat>
        <Card sx={{ flex: 1.1, minWidth: 170, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
          {emoScore != null
            ? <MiniGauge score={emoScore} label={emoLabel} />
            : <Typography sx={{ fontSize: 12, color: MUTED }}>{T('Market mood unavailable', '市场情绪不可用')}</Typography>}
        </Card>
      </Box>

      {/* ── Live Fleet table (the single control surface) ── */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <SectionLabel>{T('Live Fleet', '实盘机队')}</SectionLabel>
            <Chip size="small" variant="outlined" label={isUS ? 'RX-1 · 1-min bars' : 'RX-1-CN · 5-min bars'} sx={{ height: 20, fontSize: 10 }} />
            <Chip size="small" color="success" label={T(`${enabledCount} live`, `${enabledCount} 运行中`)} sx={{ height: 20, fontSize: 10 }} />
            <Chip size="small" variant="outlined" label={isUS ? 'gate: SPY' : 'gate: CSI300'} sx={{ height: 20, fontSize: 10, color: MUTED }} />
            {isUS && <Chip size="small" variant="outlined" color="error" label={`real money · max ${cap} sh`} sx={{ height: 20, fontSize: 10 }} />}
          </Box>

          {loadingFleet ? (
            <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>{T('Ticker', '代码')}</TableCell>
                  <TableCell sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>{T('Auto-trade', '自动')}</TableCell>
                  <TableCell sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>{T('Strategy', '策略')}</TableCell>
                  <TableCell sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>{T('Position', '持仓')}</TableCell>
                  {isUS && <TableCell sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>{T('Long-term', '长线')}</TableCell>}
                  {isUS && <TableCell sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>{T('Max shares', '上限股数')}</TableCell>}
                  {isUS && <TableCell sx={{ border: 0 }} />}
                </TableRow></TableHead>
                <TableBody>
                  {groupedList.map(([sec, tks]) => (
                    <Fragment key={sec || 'all'}>
                    {sec && (
                      <TableRow>
                        <TableCell colSpan={colSpan} sx={{ py: 0.4, bgcolor: 'rgba(108,99,255,0.06)' }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: MUTED }}>
                            {sec}<Typography component="span" sx={{ color: DIM, fontWeight: 400 }}> · {tks.length}</Typography>
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {tks.map((tk) => (
                    <TableRow key={tk} hover selected={tk === selected}
                      onClick={() => setSelected(tk)} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ py: 0.6 }}>
                        <Typography component="span" sx={{ fontWeight: 700, fontSize: 13, color: tk === selected ? VIOLET : 'text.primary' }}>{tk}</Typography>
                        {nameOf(tk) && <Typography component="span" sx={{ fontSize: 11, color: DIM, ml: 0.75 }}>{nameOf(tk)}</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 0.6 }} onClick={(e) => e.stopPropagation()}>
                        <FormControlLabel sx={{ m: 0 }}
                          control={<Switch size="small" checked={!!stocks[tk].enabled} disabled={savingCfg}
                            onChange={(e) => saveConfig({ stocks: { [tk]: { enabled: e.target.checked } } })} />}
                          label={<Typography sx={{ fontSize: 11, color: stocks[tk].enabled ? OK : WARN }}>
                            {stocks[tk].enabled ? T('ON', '开') : T('PAUSED', '暂停')}</Typography>} />
                      </TableCell>
                      <TableCell sx={{ py: 0.6 }}>
                        <Tooltip arrow placement="top" title={strategies[tk]?.description || ''}>
                          <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>
                            {strategies[tk]?.name || '—'}
                          </Typography>
                        </Tooltip>
                        {strategies[tk]?.overnight && <Typography component="span" sx={{ ml: 0.75, fontSize: 10, color: MUTED }}>overnight</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 0.6 }}><PositionCell ticker={tk} /></TableCell>
                      {isUS && <TableCell sx={{ py: 0.6 }}><LongTermCell ticker={tk} /></TableCell>}
                      {isUS && (
                        <TableCell sx={{ py: 0.6 }} onClick={(e) => e.stopPropagation()}>
                          <TextField type="number" size="small" defaultValue={stocks[tk].max_shares}
                            disabled={savingCfg} sx={{ width: 76 }} inputProps={{ min: 1, max: cap }}
                            onBlur={(e) => { const v = parseInt(e.target.value, 10); if (v && v !== stocks[tk].max_shares) saveConfig({ stocks: { [tk]: { max_shares: v } } }); }} />
                        </TableCell>
                      )}
                      {isUS && (
                        <TableCell align="right" sx={{ py: 0.6 }} onClick={(e) => e.stopPropagation()}>
                          <Button size="small" color="error" disabled={savingCfg} sx={{ fontSize: 11, minWidth: 0 }}
                            onClick={() => saveConfig({ remove: [tk] })}>{T('Remove', '移除')}</Button>
                        </TableCell>
                      )}
                    </TableRow>
                    ))}
                    </Fragment>
                  ))}
                  {configuredList.length === 0 && (
                    <TableRow><TableCell colSpan={colSpan} sx={{ border: 0 }}>
                      <Typography variant="caption" color="text.secondary">{T('No stocks configured.', '未配置股票。')}</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {isUS && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.5 }}>
              <TextField size="small" placeholder={T('Add stock (e.g. NVDA)', '添加 (如 NVDA)')}
                value={newTicker} disabled={savingCfg}
                onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter' && newTicker && !stocks[newTicker]) { saveConfig({ stocks: { [newTicker]: { enabled: true, max_shares: 1 } } }); setNewTicker(''); } }}
                sx={{ width: 180 }} />
              <Button size="small" variant="outlined" disabled={savingCfg || !newTicker || !!stocks[newTicker]}
                onClick={() => { saveConfig({ stocks: { [newTicker]: { enabled: true, max_shares: 1 } } }); setNewTicker(''); }}>
                {T('Add', '添加')}
              </Button>
            </Box>
          )}
          {cfgMsg && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: cfgMsg.startsWith('Error') ? DOWN : OK }}>{cfgMsg}</Typography>
          )}
        </CardContent>
      </Card>

      {/* ── Selected-ticker detail (drill-down) ── */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <SectionLabel>{selected ? labelOf(selected) : T('Select a stock', '选择股票')}</SectionLabel>
            <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => { if (v) setDays(v); }}>
              <ToggleButton value={1} sx={{ px: 1.25, fontSize: '0.7rem' }}>1D</ToggleButton>
              <ToggleButton value={7} sx={{ px: 1.25, fontSize: '0.7rem' }}>7D</ToggleButton>
              <ToggleButton value={30} sx={{ px: 1.25, fontSize: '0.7rem' }}>30D</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {!selected ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              {T('Click a stock in the fleet above to see its chart and recent activity.', '点击上方机队中的股票查看其走势与近期信号。')}
            </Typography>
          ) : (
            <>
              {/* status strip */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 10, color: MUTED, letterSpacing: 0.5 }}>{T('POSITION', '持仓')}</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: position ? (position.side === 'long' ? OK : DOWN) : MUTED }}>
                    {position ? `${position.side.toUpperCase()} @ ${ccy}${position.entry_price.toFixed(2)}` : T('FLAT', '空仓')}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10, color: MUTED, letterSpacing: 0.5 }}>{T('TODAY', '今日')}</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: today.pnl_pct >= 0 ? OK : DOWN }}>
                    {`${today.pnl_pct >= 0 ? '+' : ''}${today.pnl_pct?.toFixed(2) || '0.00'}% · ${today.trades || 0} tr`}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10, color: MUTED, letterSpacing: 0.5 }}>{T('CUMULATIVE', '累计')}</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: cumulative.pnl_pct >= 0 ? OK : DOWN }}>
                    {`${cumulative.pnl_pct >= 0 ? '+' : ''}${cumulative.pnl_pct?.toFixed(2) || '0.00'}% · ${cumulative.total_trades || 0} tr · ${cumulative.win_rate || 0}% WR`}
                  </Typography>
                </Box>
              </Box>

              {/* equity curve */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{T('Cumulative P&L', '累计盈亏')}</Typography>
              {equityCurve.length > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={equityCurve}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                    <XAxis dataKey="timestamp" stroke={MUTED} tick={{ fontSize: 10 }} tickFormatter={(v) => v.substring(5, 10)} />
                    <YAxis stroke={MUTED} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`} />
                    <RTooltip contentStyle={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8 }}
                      formatter={(v) => [`${v >= 0 ? '+' : ''}${v.toFixed(2)}%`, T('Cumulative', '累计')]} />
                    <Line type="monotone" dataKey="pnl_pct" stroke="#58a6ff" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2.5 }}>
                  {T('No trades recorded yet', '暂无交易记录')}
                </Typography>
              )}

              {/* recent activity */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>{T('Recent Activity', '近期信号')}</Typography>
              {detailSignals.length > 0 ? (
                <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none', maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>{T('Time', '时间')}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>{T('Action', '动作')}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>{T('Price', '价格')}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: MUTED, py: 0.5 }}>P&L</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {detailSignals.map((s, i) => {
                        const pnl = s.trade?.pnl_pct;
                        const ac = s.action === 'BUY' ? OK : s.action === 'SELL' ? DOWN : WARN;
                        return (
                          <TableRow key={i}>
                            <TableCell sx={{ fontSize: 12, color: MUTED, py: 0.5 }}>{s.timestamp?.substring(5, 16)}</TableCell>
                            <TableCell sx={{ fontSize: 12, fontWeight: 700, color: ac, py: 0.5 }}>{s.action}</TableCell>
                            <TableCell sx={{ fontSize: 12, py: 0.5 }}>{ccy}{s.price?.toFixed(2)}</TableCell>
                            <TableCell sx={{ fontSize: 12, fontWeight: 600, py: 0.5, color: pnl > 0 ? OK : pnl < 0 ? DOWN : MUTED }}>
                              {pnl != null ? `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}%` : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2.5 }}>
                  {T(`No activity in the last ${days} day(s)`, `近 ${days} 天无信号`)}
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>
      </>)}
    </Box>
  );
}
